<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import WhatsAppPaymentOrderCard from '../WhatsAppPaymentOrderCard.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import MessageApi from 'dashboard/api/inbox/message';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const {
  content,
  attachments,
  contentAttributes,
  additionalAttributes,
  messageType,
  whatsappOrderStatus,
  whatsappPaymentStatus,
  whatsappPaymentOrder,
  conversationId,
  id,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);
const { t } = useI18n();
const $store = useStore();

const renderOriginal = ref(false);
const isUpdatingStatus = ref(false);

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const readPath = (object, path) => {
  if (!object) return undefined;
  return path.split('.').reduce((currentValue, key) => {
    if (currentValue === undefined || currentValue === null) return undefined;
    return currentValue[key];
  }, object);
};

const normalizeList = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const normalizeAmount = amount => {
  if (amount && typeof amount === 'object') {
    return firstPresent(amount.value, amount.amount, amount.cents);
  }
  return amount;
};

const normalizePaymentMethod = type => {
  const normalized = String(type || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

  if (normalized === 'pix' || normalized === 'pix_dynamic_code') {
    return 'pix_dynamic_code';
  }
  if (normalized === 'boleto') return 'boleto';
  if (normalized === 'link' || normalized === 'payment_link') {
    return 'payment_link';
  }
  return normalized;
};

const sessionOrderDetails = computed(() => {
  const interactiveParams =
    readPath(contentAttributes.value, 'interactive.action.parameters') || {};
  const persistedOrder = whatsappPaymentOrder?.value || {};
  const additional = additionalAttributes?.value || {};
  const referenceId = firstPresent(
    interactiveParams.reference_id,
    interactiveParams.referenceId,
    persistedOrder.reference_id,
    persistedOrder.referenceId
  );
  const interactiveType = firstPresent(
    readPath(contentAttributes.value, 'interactive.type'),
    readPath(contentAttributes.value, 'type')
  );

  if (interactiveType !== 'order_details' && !referenceId) return null;

  const paymentSettings = normalizeList(
    firstPresent(
      interactiveParams.payment_settings,
      interactiveParams.paymentSettings,
      persistedOrder.payment_settings,
      persistedOrder.paymentSettings,
      additional.payment_settings,
      additional.paymentSettings
    )
  );

  return {
    reference_id: referenceId,
    total_amount: normalizeAmount(
      firstPresent(
        interactiveParams.total_amount,
        interactiveParams.totalAmount,
        persistedOrder.total_amount,
        persistedOrder.totalAmount
      )
    ),
    order_status: firstPresent(
      whatsappOrderStatus?.value,
      persistedOrder.order_status,
      persistedOrder.orderStatus,
      readPath(interactiveParams, 'order.status')
    ),
    payment_status: firstPresent(
      whatsappPaymentStatus?.value,
      persistedOrder.payment_status,
      persistedOrder.paymentStatus,
      readPath(interactiveParams, 'payment.status')
    ),
    payment_settings: paymentSettings,
    payment_methods:
      persistedOrder.payment_methods ||
      persistedOrder.paymentMethods ||
      paymentSettings.map(setting => normalizePaymentMethod(setting.type)),
  };
});

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const hasExternalError = computed(
  () => !!contentAttributes.value?.externalError
);

const isExternalErrorOnly = computed(() => {
  return (
    hasExternalError.value &&
    !content.value &&
    !attachments.value?.length &&
    !sessionOrderDetails.value
  );
});

const isEmpty = computed(() => {
  return (
    !isExternalErrorOnly.value &&
    !content.value &&
    !attachments.value?.length &&
    !sessionOrderDetails.value
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

const updateOrderStatus = async (orderStatus, paymentStatus = null) => {
  const referenceId = sessionOrderDetails.value?.reference_id;
  if (!referenceId || isUpdatingStatus.value) return;

  isUpdatingStatus.value = true;
  try {
    const response = await MessageApi.updateOrderStatus(
      conversationId.value,
      id.value,
      referenceId,
      orderStatus,
      paymentStatus
    );
    if (response.data && response.data.message) {
      $store.dispatch('updateMessage', response.data.message);
    }
    useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_STATUS_ERROR'));
  } finally {
    isUpdatingStatus.value = false;
  }
};
</script>

<template>
  <span
    v-if="isExternalErrorOnly"
    class="hidden"
    data-test-id="external-error-only-placeholder"
  />
  <BaseBubble v-else class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <WhatsAppPaymentOrderCard
        v-if="sessionOrderDetails"
        :order="sessionOrderDetails"
        :is-updating-status="isUpdatingStatus"
        @update-order-status="updateOrderStatus"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
